import { Box, Flex, HStack, Text } from "@chakra-ui/react";
import * as React from "react";

import { InfoTip } from "./info-tip";

export interface MetricCardProps {
  label: React.ReactNode;
  info?: React.ReactNode;
  /** Extra content rendered before the value (icon circle, …). */
  adornment?: React.ReactNode;
  /** Extra content rendered after the value (delta badge, …). */
  badge?: React.ReactNode;
  value?: React.ReactNode;
  sub?: React.ReactNode;
  meter?: number; // 0-100
  standalone?: boolean;
}

// A single stat: standalone card (`standalone`) or an item inside a
// MetricGroup.
export function MetricCard({
  adornment,
  badge,
  info,
  label,
  meter,
  standalone,
  sub,
  value,
}: MetricCardProps) {
  return (
    <Flex
      background={standalone ? "white" : undefined}
      borderRadius={standalone ? "l3" : undefined}
      boxShadow={standalone ? "card" : undefined}
      direction="column"
      flex="1"
      justify="center"
      minHeight={standalone ? ("122px" as any) : undefined}
      minWidth="0"
      padding="4"
    >
      <HStack
        align="flex-start"
        color="fg.muted"
        fontSize="sm"
        justify="space-between"
        lineHeight={"20px" as any}
        marginBottom="2.5"
        minHeight="5"
      >
        <Text>{label}</Text>
        {info && <InfoTip content={info} />}
      </HStack>
      <HStack gap="3" height="9">
        {adornment}
        {value !== undefined && (
          <Text color="fg.ink" textStyle="metricNumber" whiteSpace="nowrap">
            {value}
          </Text>
        )}
        {badge}
      </HStack>
      {meter !== undefined && (
        <Box
          background="meterTrack"
          borderRadius="full"
          height="1.5"
          marginTop="2.5"
          maxWidth={"220px" as any}
          overflow="hidden"
        >
          <Box
            background="accent"
            borderRadius="full"
            height="full"
            width={`${Math.max(0, Math.min(100, meter))}%` as any}
          />
        </Box>
      )}
      {sub && (
        <Text
          color="fg.faded"
          fontSize={"13px" as any}
          lineHeight={"19px" as any}
          marginTop={meter !== undefined ? "2" : "1.5"}
        >
          {sub}
        </Text>
      )}
    </Flex>
  );
}

export function MetricGroup({ children }: { children: React.ReactNode }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <Flex
      align="stretch"
      background="white"
      borderRadius="l3"
      boxShadow="card"
      direction={{ base: "column", lg: "row" }}
    >
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Box
              background="border.soft"
              flexShrink="0"
              marginX={{ base: "4", lg: "0" }}
              marginY={{ base: "0", lg: "4" }}
              height={{ base: "1px" as any, lg: "auto" }}
              width={{ base: "auto", lg: "1px" as any }}
            />
          )}
          {child}
        </React.Fragment>
      ))}
    </Flex>
  );
}
